package link.erpango.quip.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import kotlinx.browser.window
import link.erpango.quip.GOOGLE_WEB_CLIENT_ID
import link.erpango.quip.core.auth.entities.GoogleUser
import link.erpango.quip.core.auth.google.CredentialResponse
import link.erpango.quip.core.auth.google.GoogleAuthProvider
import link.erpango.quip.core.auth.google.GoogleIdConfiguration
import link.erpango.quip.core.auth.google.google
import org.w3c.dom.MessageEvent
import org.w3c.dom.events.Event
import kotlin.random.Random

@Composable
actual fun GoogleButtonUiContainer(
    modifier: Modifier,
    onGoogleSignInResult: (GoogleUser?) -> Unit,
    content: @Composable GoogleButtonUiContainerScope.() -> Unit,
    googleAuthProvider: GoogleAuthProvider
) {
    // Listen for messages from OAuth popup callback
    DisposableEffect(Unit) {
        val messageListener: (Event) -> Unit = { event ->
            try {
                val messageEvent = event as? MessageEvent
                if (messageEvent != null &&
                    messageEvent.origin == window.location.origin
                ) {
                    val data = messageEvent.data.asDynamic()
                    val dataType = data?.type as? String
                    // Use bracket notation for JS property access (not Kotlin's .get())
                    val token = js("data && data['id_token'] ? data['id_token'] : null") as? String
                    if (dataType == "google-oauth-callback" && token != null) {
                        console.log("GoogleButtonUiContainer: Received ID token from popup. Length: ${token.length}")
                        val user = GoogleUser(
                            idToken = token,
                            displayName = "Google User",
                            profilePicUrl = null
                        )
                        onGoogleSignInResult(user)
                    }
                }
            } catch (e: dynamic) {
                console.error("GoogleButtonUiContainer: Error processing message:", e)
            }
        }
        window.addEventListener("message", messageListener)
        onDispose {
            window.removeEventListener("message", messageListener)
        }
    }

    // Initialize GSI for auto-select / One Tap (if it works, great; if not, popup is the fallback)
    LaunchedEffect(Unit) {
        val callback = { response: CredentialResponse ->
            val idToken = response.credential
            console.log("GoogleButtonUiContainer: GSI credential callback. Token length: ${idToken.length}")
            val user = GoogleUser(
                idToken = idToken,
                displayName = "Google User",
                profilePicUrl = null
            )
            onGoogleSignInResult(user)
        }

        fun initializeGsi() {
            try {
                val config = js("{}").unsafeCast<GoogleIdConfiguration>()
                config.client_id = GOOGLE_WEB_CLIENT_ID
                config.callback = callback
                config.auto_select = false
                config.use_fedcm_for_prompt = true

                console.log("GoogleButtonUiContainer: Initializing GSI with Client ID: ${config.client_id}")
                google.accounts.id.initialize(config)
                console.log("GoogleButtonUiContainer: GSI initialized successfully")
            } catch (e: dynamic) {
                console.error("GoogleButtonUiContainer: Error initializing GSI:", e)
            }
        }

        if (js("!!(typeof google !== 'undefined' && google.accounts && google.accounts.id)") as Boolean) {
            initializeGsi()
        } else {
            console.log("GoogleButtonUiContainer: google.accounts.id not ready, waiting...")
            var attempts = 0
            var intervalId = 0
            intervalId = window.setInterval({
                attempts++
                if (js("!!(typeof google !== 'undefined' && google.accounts && google.accounts.id)") as Boolean) {
                    console.log("GoogleButtonUiContainer: google.accounts.id ready after $attempts attempts")
                    window.clearInterval(intervalId)
                    initializeGsi()
                } else if (attempts > 50) {
                    console.warn("GoogleButtonUiContainer: GSI script did not load in time")
                    window.clearInterval(intervalId)
                }
            }, 100)
        }
    }

    // On click: open OAuth popup DIRECTLY (synchronous from user gesture = works on Safari)
    val uiContainerScope = remember {
        object : GoogleButtonUiContainerScope {
            override fun onClick() {
                console.log("GoogleButtonUiContainer: Opening OAuth popup")
                openOAuthPopup()
            }
        }
    }

    // Render the Compose content (QuipSecondaryButton) directly
    uiContainerScope.content()
}

private fun openOAuthPopup() {
    val clientId = GOOGLE_WEB_CLIENT_ID
    if (clientId.isBlank()) {
        console.error("GoogleButtonUiContainer: Cannot open OAuth popup - no client ID configured")
        return
    }

    val redirectUri = "${window.location.origin}/oauth-callback.html"
    val nonce = Random.nextLong().toString(36) + Random.nextLong().toString(36)

    val authUrl = "https://accounts.google.com/o/oauth2/v2/auth" +
        "?client_id=${encodeURIComponent(clientId)}" +
        "&redirect_uri=${encodeURIComponent(redirectUri)}" +
        "&response_type=id_token" +
        "&scope=openid%20email%20profile" +
        "&nonce=$nonce" +
        "&prompt=select_account"

    console.log("GoogleButtonUiContainer: OAuth redirect URI: $redirectUri")

    val width = 500
    val height = 600
    val left = (window.screen.width - width) / 2
    val top = (window.screen.height - height) / 2

    window.open(
        authUrl,
        "google-sign-in",
        "width=$width,height=$height,left=$left,top=$top,toolbar=no,menubar=no,scrollbars=yes"
    )
}

private fun encodeURIComponent(value: String): String =
    js("encodeURIComponent(value)") as String
